import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { toast, ToastContainer } from 'react-toastify';
import { requestCameraPermission } from '../utils/requestCameraPermission';
import {
  clearCurrentUserProfile,
  getPostVerificationRoute,
  loadUserProfile,
  persistCurrentUserProfile,
} from '../utils/authVerification';
import RegistrationProgress from '../components/RegistrationProgress';
import 'react-toastify/dist/ReactToastify.css';
import './VerifyId.css';

const ALLOWED_TYPES  = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];

function validateFile(file) {
  if (!file) return 'No file selected.';
  if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(jpe?g|png|heic|wepp)$/i)) {
    return 'Only JPEG or PNG images are accepted.';
  }
  return null;
}

const resizeImage = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      
      const MAX_WIDTH = 1200;
      let scale = MAX_WIDTH / img.width;
      if (scale > 1) scale = 1; // don't upscale if already small

      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => resolve(blob),
        "image/jpeg",
        0.7 // 70% quality compression
      );
    };

    reader.readAsDataURL(file);
  });
};

export default function VerifyId() {
  const navigate = useNavigate();
  const functions = getFunctions();
  const verifyUserIdentity = httpsCallable(functions, 'verifyUserIdentityV2');
  const isNativePlatform = Capacitor.isNativePlatform();

  const [uiState, setUiState]       = useState('loading'); // loading|idle|uploading|processing|approved|review|rejected
  const [idFile, setIdFile]         = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [idPreview, setIdPreview]   = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [similarity, setSimilarity] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(false);
  const [stream, setStream] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const idInputRef     = useRef(null);
  const selfieInputRef = useRef(null);
  const stopCameraStream = () => {
    if (!streamRef.current) return;
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  };

  const setSelfieSelection = (file) => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return false;
    }

    setSelfieFile(file);
    setSelfiePreview((current) => {
      if (current && current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return URL.createObjectURL(file);
    });
    return true;
  };
  const videoRef = useRef(null);
  const streamRef = useRef(null);


  // ── On mount: check existing verification status ──────────────────────────
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const user = auth.currentUser;
      if (!user) {
        clearCurrentUserProfile();
        navigate('/login', { replace: true });
        return;
      }

      try {
        await user.reload();
        const profileData = await loadUserProfile(user.uid);
        if (!profileData) {
          clearCurrentUserProfile();
          toast.error('User profile not found. Please sign in again.');
          navigate('/login', { replace: true });
          return;
        }

        if (cancelled) return;

        const nextRoute = getPostVerificationRoute(user, profileData);
        if (nextRoute !== '/verify-id') {
          persistCurrentUserProfile(user.uid, profileData);
          navigate(nextRoute, { replace: true });
          return;
        }

        persistCurrentUserProfile(user.uid, profileData);

        const verSnap = await getDoc(doc(db, 'verifications', user.uid));
        const count = verSnap.exists() ? (verSnap.data().submissionCount || 0) : 0;
        setSubmissionCount(count);

        const { verificationStatus } = profileData;
        if (verificationStatus === 'review') {
          setUiState('review');
        } else if (verificationStatus === 'rejected') {
          setUiState('rejected');
        } else {
          setUiState('idle');
        }
      } catch (e) {
        console.error('[VerifyId] status check error:', e);
        setUiState('idle');
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    setCameraSupported(
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );
  }, []);

  useEffect(() => () => {
    if (!streamRef.current) return;
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => {
    if (!stream || !videoRef.current) return;
    videoRef.current.srcObject = stream;
    videoRef.current.play().catch((playErr) => {
      console.error('[VerifyId] Failed to start camera preview:', playErr);
    });
  }, [stream]);

  // ── File selection helpers ─────────────────────────────────────────────────
  const handleFileSelect = (setter, previewSetter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    if (setter === setSelfieFile) {
      setSelfieSelection(file);
      return;
    }
    setter(file);
    previewSetter(URL.createObjectURL(file));
  };

  const openSelfieCamera = async () => {
    if (stream) {
      setCameraOpen(true);
      return;
    }
    if (!cameraSupported) {
      toast.error('Camera capture is not available on this device.');
      return;
    }

    setCameraLoading(true);
    setCameraReady(false);
    try {
      if (isNativePlatform && Capacitor.getPlatform() === 'android') {
        const granted = await requestCameraPermission();
        if (!granted) {
          toast.error('Camera permission is required to take a selfie.');
          return;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      stopCameraStream();
      streamRef.current = stream;
      setStream(stream);
      setCameraOpen(true);
    } catch (err) {
      console.error('[VerifyId] Camera open error:', err);
      toast.error('Unable to access the camera. Check camera permissions.');
    } finally {
      setCameraLoading(false);
    }
  };

  const closeSelfieCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraReady(false);
  };

  const captureSelfie = async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const width = video.videoWidth || 720;
    const height = video.videoHeight || 960;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      toast.error('Camera capture failed. Please try again.');
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.9);
    });

    if (!blob) {
      toast.error('Camera capture failed. Please try again.');
      return;
    }

    const file = new File([blob], `selfie-${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });

    if (setSelfieSelection(file)) {
      closeSelfieCamera();
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!idFile || !selfieFile) {
      toast.error('Please select both your ID photo and selfie before submitting.');
      return;
    }

    const user = auth.currentUser;
    if (!user) { toast.error('Session expired. Please sign in again.'); return; }

    try {
      // Upload phase
      setUiState('uploading');
      const uid = user.uid;

      const compressedId = await resizeImage(idFile);
      const compressedSelfie = await resizeImage(selfieFile);

      await Promise.all([
        uploadBytes(ref(storage, `verification/${uid}/id.jpg`),     compressedId),
        uploadBytes(ref(storage, `verification/${uid}/selfie.jpg`), compressedSelfie),
      ]);

      // Verification phase
      setUiState('processing');
      const result = await verifyUserIdentity({ userId: uid });
      const { verificationStatus, faceSimilarity, nameMatch } = result.data;

      setSimilarity(faceSimilarity);
      setSubmissionCount((c) => c + 1);

      if (verificationStatus === 'approved') {
        setUiState('approved');
        toast.success('Verification successful.');
      } else if (verificationStatus === 'review') {
        setUiState('review');
        toast.info('Your ID is under manual review. Please check back later.');
      } else {
        setUiState('rejected');
        toast.error('ID verification was not successful. Please try again with clearer photos.');
      }

      console.log('[VerifyId] result:', { verificationStatus, faceSimilarity, nameMatch });

    } catch (err) {
      console.error('[VerifyId] submit error:', err);
      const msg = err?.message || 'Verification failed. Please try again.';
      toast.error(msg);
      setUiState(submissionCount >= 1 ? 'rejected' : 'idle');
    }
  };

  // ── Reset for retry ────────────────────────────────────────────────────────
  const handleRetry = () => {
    setIdFile(null);
    setSelfieFile(null);
    setIdPreview(null);
    setSelfiePreview(null);
    setSimilarity(null);
    setUiState('idle');
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      setShowLogoutModal(false);
      await auth.signOut();
      clearCurrentUserProfile();
      localStorage.removeItem('pendingEmail');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[VerifyId] logout error:', error);
      toast.error('Unable to log out right now. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleContinueToSignIn = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      await auth.signOut();
      clearCurrentUserProfile();
      localStorage.removeItem('pendingEmail');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[VerifyId] continue to sign in error:', error);
      toast.error('Unable to continue to sign in right now. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const renderLogoutSection = () => (
    <div className="mt-4 w-full border-t border-slate-200 pt-4">
      <button
        type="button"
        onClick={() => setShowLogoutModal(true)}
        disabled={isLoggingOut}
        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
      >
        {isLoggingOut ? 'Logging out...' : 'Log out'}
      </button>
    </div>
  );

  const renderLogoutModal = () => {
    if (!showLogoutModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
          <h2 className="text-lg font-semibold text-slate-900">Log out?</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Your progress is saved. When you sign back in, you'll return to Identity Verification to complete your registration.
          </p>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Stay here
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (uiState === 'loading') {
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-spinner" />
          <p className="vid-status-text">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (uiState === 'approved') {
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-icon vid-icon--success">✓</div>
          <h2 className="vid-title">Verification Successful</h2>
          <button
            type="button"
            onClick={handleContinueToSignIn}
            disabled={isLoggingOut}
            className="vid-btn vid-btn--primary mt-4 w-full"
          >
            Continue to Sign In
          </button>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  if (uiState === 'review') {
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-icon vid-icon--review">⏳</div>
          <h2 className="vid-title">Under Review</h2>
          <p className="vid-subtitle">
            Your submission has been sent for manual review.<br />
            Please check back later for an update.<br />
            You can leave this page - your progress is saved.
          </p>
          <p className="vid-contact">
            Questions? Contact us at{' '}
            <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>
          </p>
          {renderLogoutSection()}
          </div>
        </div>
        {renderLogoutModal()}
        <ToastContainer />
      </div>
    );
  }

  if (uiState === 'rejected' && submissionCount >= 2) {
    // 2nd rejection → forced review (no retry UI)
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-icon vid-icon--review">⏳</div>
          <h2 className="vid-title">Under Manual Review</h2>
          <p className="vid-subtitle">
            We were unable to automatically verify your identity after two attempts.<br />
            Your submission has been sent for manual review.<br />
            Please check back later for an update.<br />
            You can leave this page - your progress is saved.
          </p>
          <p className="vid-contact">
            Questions? Contact us at{' '}
            <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>
          </p>
          {renderLogoutSection()}
          </div>
        </div>
        {renderLogoutModal()}
        <ToastContainer />
      </div>
    );
  }

  if (uiState === 'rejected') {
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-icon vid-icon--reject">✕</div>
          <h2 className="vid-title">Verification Failed</h2>
          <p className="vid-subtitle">
            We could not verify your identity. Please try again with clearer photos.
          </p>
          {similarity !== null && (
            <p className="vid-score">Face match score: {Number(similarity).toFixed(0)}%</p>
          )}
          <div className="vid-tips">
            <p className="vid-tips-title">Tips for a successful submission:</p>
            <ul>
              <li>Ensure your full name is clearly visible on the ID</li>
              <li>Take your selfie in good lighting — no sunglasses or hats</li>
              <li>Avoid glare or shadows on the ID photo</li>
              <li>Only one face should be visible in your selfie</li>
            </ul>
          </div>
          <button className="vid-btn vid-btn--primary" onClick={handleRetry}>
            Try Again (1 attempt remaining)
          </button>
          <p className="vid-contact">
            Still having trouble? Contact us at{' '}
            <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>
          </p>
          {renderLogoutSection()}
          </div>
        </div>
        {renderLogoutModal()}
        <ToastContainer />
      </div>
    );
  }

  if (uiState === 'uploading' || uiState === 'processing') {
    return (
      <div className="verify-id-screen">
        <div className="flex w-full max-w-[480px] flex-col gap-4">
          <RegistrationProgress currentStep="id" tone="dark" />
          <div className="verify-id-card">
          <div className="vid-spinner" />
          <p className="vid-status-text">
            {uiState === 'uploading'
              ? 'Uploading your documents securely…'
              : 'Verifying your identity…'}
          </p>
          <p className="vid-status-sub">This may take up to 30 seconds. Please do not close the app.</p>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  // ── Idle: upload form ──────────────────────────────────────────────────────
  return (
    <div className="verify-id-screen">
      <div className="flex w-full max-w-[480px] flex-col gap-4">
        <RegistrationProgress currentStep="id" tone="dark" />
        <div className="verify-id-card">
        <img
          src="/coldwire-logo.png"
          onError={(event) => { event.currentTarget.src = '/icon.png'; }}
          alt="ColdWire"
          className="vid-logo"
        />
        <h1 className="vid-title">Identity Verification</h1>
        <p className="vid-subtitle">
          ColdWire is restricted to verified cold case investigators and related professionals.<br />
          Please upload your ID credentials and a selfie to complete registration.
        </p>

        {/* Instructions */}
        <div className="vid-instructions">
          <p className="vid-instructions-title">📋 Before you start:</p>
          <ul>
            <li>Use a valid <b>government-issued or professional ID</b></li>
            <li>Your full name must be clearly visible on the ID</li>
            <li>Take a clear selfie — no sunglasses, hats, or masks</li>
	    <li>Keep hair away from your face so it’s fully visible</li>
            <li>Use good lighting; avoid glare on the ID</li>
            
          </ul>
	  <hr style={{ margin: '10px 0' }} />

<p style={{ fontSize: '13px', color: '#555' }}>
  Your information is used for <b>identity verification only</b> and is <b>not shared</b> with third parties. ID images are deleted within 96 hours of a decision.
</p>
        </div>

        {/* ID Upload */}
        <div className="vid-upload-section">
          <p className="vid-upload-label">📄 Department ID</p>
          <div
            className={`vid-upload-box ${idPreview ? 'vid-upload-box--filled' : ''}`}
            onClick={() => idInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && idInputRef.current?.click()}
          >
            {idPreview
              ? <img src={idPreview} alt="ID preview" className="vid-preview-img" />
              : <span className="vid-upload-placeholder">Tap to upload ID photo</span>}
          </div>
          <input
            ref={idInputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="vid-hidden-input"
            onChange={handleFileSelect(setIdFile, setIdPreview)}
          />
          {idFile && (
            <p className="vid-file-name">📎 {idFile.name} ({(idFile.size / 1024 / 1024).toFixed(1)} MB)</p>
          )}
        </div>

        {/* Selfie Capture */}
        <div className="vid-upload-section">
          <p className="vid-upload-label">🤳 Selfie Photo</p>
          <div
            className={`vid-upload-box ${selfiePreview ? 'vid-upload-box--filled' : ''}`}
            onClick={() => {
              if (isNativePlatform && cameraSupported) {
                openSelfieCamera();
                return;
              }
              selfieInputRef.current?.click();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (isNativePlatform && cameraSupported) {
                openSelfieCamera();
                return;
              }
              selfieInputRef.current?.click();
            }}
          >
            {selfiePreview
              ? <img src={selfiePreview} alt="Selfie preview" className="vid-preview-img" />
              : <span className="vid-upload-placeholder">Tap to take selfie</span>}
          </div>
          <input
            ref={selfieInputRef}
            type="file"
            accept="image/jpeg,image/png"
            capture="user"
            className="vid-hidden-input"
            onChange={handleFileSelect(setSelfieFile, setSelfiePreview)}
          />
                    {/*
	    <div className="vid-action-row">
            {!isNativePlatform && cameraSupported && (
              <button
                type="button"
                className="vid-btn vid-btn--secondary"
                onClick={openSelfieCamera}
                disabled={cameraLoading}
              >
                {cameraLoading ? 'Opening camera...' : 'Use Camera'}
              </button>
            )}
            {isNativePlatform && (
              <button
                type="button"
                className="vid-btn vid-btn--ghost"
                onClick={() => selfieInputRef.current?.click()}
              >
                Choose Existing Photo Instead
              </button>
            )}
          </div>
	  */}
          {selfieFile && (
            <p className="vid-file-name">📎 {selfieFile.name} ({(selfieFile.size / 1024 / 1024).toFixed(1)} MB)</p>
          )}
        </div>

        {/* Submit */}
        <button
          className={`vid-btn vid-btn--primary ${(!idFile || !selfieFile) ? 'vid-btn--disabled' : ''}`}
          onClick={handleSubmit}
          disabled={!idFile || !selfieFile}
        >
          Submit for Verification
        </button>
        <p className="vid-privacy">
          🔒 Your photos are encrypted and stored securely. ID photos will be automatically deleted
          within 96 hours of your account decision. We never share your ID with third parties.
        </p>
        {renderLogoutSection()}
        </div>
      </div>
      {renderLogoutModal()}
      {cameraOpen && (
        <div className="vid-camera-overlay" role="dialog" aria-modal="true" aria-label="Selfie camera">
          <div className="vid-camera-modal">
            <h2 className="vid-camera-title">Take Selfie</h2>
            <p className="vid-camera-subtitle">
              Center your face in the frame and make sure the area is well lit.
            </p>
            <div className="vid-camera-frame">
              {stream && (
                <video
                  ref={videoRef}
                  className="vid-camera-preview"
                  playsInline
                  muted
                  autoPlay
                  onLoadedMetadata={() => setCameraReady(true)}
                />
              )}
              {!cameraReady && (
                <div className="vid-camera-loading">Starting camera...</div>
              )}
            </div>
            <div className="vid-camera-actions">
              <button
                type="button"
                className="vid-btn vid-btn--ghost"
                onClick={closeSelfieCamera}
              >
                Cancel
              </button>
              <button
                type="button"
                className="vid-btn vid-btn--primary"
                onClick={captureSelfie}
                disabled={!cameraReady}
              >
                Capture Selfie
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}
